import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import _ from "lodash";
import cx from "classnames";
import { Form, Tab, Nav } from "react-bootstrap";
import Tooltip from "components/Tooltip";

import { EditorContext } from "components/Editor";
import { updateWidgetProperty } from "actions/editor";
import { Font } from "components/Editor/components/properties/fonts";
import { Fonts } from '@paperflite-marketing/cleverstory-commons';
import { Property } from "components/Editor/components/properties";

import { associateFontsWithWigdet } from "actions/fonts";

import styles from "./properties.module.scss";

export const FontBackgroundSettings = React.memo(
  ({ id, widget, breakpoint, modal = false, handlePropertyChange }) => {
    const dispatch = useDispatch();

    const editorContext = React.useContext(EditorContext);
    const editorSession = editorContext.editorSession.id;

    const deviceProperties = _.get(widget, `properties.${breakpoint}`, {});
    const fontStyles = _.get(deviceProperties, "font", {});
    const fontFamily = _.get(deviceProperties, 'font.fontFamily', 'Montserrat,sans-serif');
    const primaryColor = _.get(fontStyles, "color", "#000000");
    const secondaryColor = _.get(fontStyles, "secondaryColor", "#000000");
    const tertiaryColor = _.get(fontStyles, "tertiaryColor", "#000000");
    const fontSize = _.get(fontStyles, "fontSize", 16);
    
    Fonts.useFont(_.get(widget, 'fonts') || []);

    const handleFontStylesChange = (type, value) => {

      handlePropertyChange("font", {
        ...fontStyles,
        [type]: value,
      });
    };

    const handleFontFamily = (value) => {
      var font = _.get(value, "font.id");
      var family = _.get(value, "font.fontFamily");
      if (!family) {
        return;
      }

      var request = {
        ...fontStyles,
        fontFamily: family,
      };

      handlePropertyChange("font", request);
      if (font) {
        dispatch(associateFontsWithWigdet(editorSession, id, [font]));
      }
    };

    return (
      <>
        {(breakpoint === "desktop" || modal) && (
          <Font.Family value={fontFamily}
          onChange={handleFontFamily} 
          className={styles.buttonStyleProperty}/>
        )}
        <Property name="Font Size">
          <Property.Slider
            min={10}
            max={16}
            step={1}
            input
            value={fontSize}
            onChange={(value) => {
              handleFontStylesChange("fontSize", value);
            }}
          />
        </Property>
        {(breakpoint === "desktop" || modal) && (
          <>
            <Property.Color
              title={"Primary Font Color"}
              value={primaryColor}
              onChange={(color) => {
                handleFontStylesChange("color", color);
              }}
            />
            <Property.Color
              title={"Secondary Font Color"}
              value={secondaryColor}
              onChange={(color) => {
                handleFontStylesChange("secondaryColor", color);
              }}
            />
            <Property.Color
              title={"Placeholder Font Color"}
              value={tertiaryColor}
              onChange={(color) => {
                handleFontStylesChange("tertiaryColor", color);
              }}
            />
          </>
        )}
      </>
    );
  }
);

const PlaceholderStyleOptions = ({
  breakpoint,
  fontStyles1,
  fontFamily,
  tertiaryColor,
  fontColor,
  fontSize,
  lineHeight,
  letterSpacing,
  alignment,
  handleFontStylesChange,
  handleFontFamily,
  handleAlignmentChange,
  handleFontStyleToggle,
}) => {
  return (
    <>
      <Property.Color
        title="Font Color"
        value={tertiaryColor}
        onChange={(color) => handleFontStylesChange(fontColor, color)}
      />

      {breakpoint === "desktop" && (
        <div className={styles.propertyItem}>
          <Font.Family
            name={"Font Style"}
            value={fontFamily}
            onChange={handleFontFamily}
          />
        </div>
      )}

      <Property name="Font Size" className={styles.propertyItem}>
        <Property.Slider
          min={10}
          max={24}
          step={1}
          input
          value={fontSize}
          onChange={(value) => handleFontStylesChange("fontSize", value)}
        />
      </Property>

      {fontColor !== "tertiaryColor" && 
        <Property name="Line Spacing" className={styles.propertyItem}>
          <Property.Slider
            input
            min={0.5}
            max={3.0}
            step={0.1}
            value={lineHeight}
            onChange={(value) => handleFontStylesChange("lineSpacing", value)}
          />
        </Property>
      }

      <Property name="Character Spacing" className={styles.propertyItem}>
        <Property.Slider
          min={0}
          max={10}
          step={0.5}
          input
          value={letterSpacing}
          onChange={(value) => handleFontStylesChange("letterSpacing", value)}
        />
      </Property>

      <Property name="Styles" className={styles.propertyItem}>
        <div className={styles.fontActions}>
          <Property.Action
            className={cx(styles.iconButton, {
              [styles.active]: _.get(fontStyles1, 'bold', false),
            })}
            onClick={() => handleFontStyleToggle('bold')}
            tooltip="Bold"
          >
            <i className="cleverstory-bold" />
          </Property.Action>

          <Property.Action
            className={cx(styles.iconButton, {
              [styles.active]: _.get(fontStyles1, 'italic', false),
            })}
            onClick={() => handleFontStyleToggle('italic')}
            tooltip="Italic"
          >
            <i className="cleverstory-italics" />
          </Property.Action>

          <Property.Action
            className={cx(styles.iconButton, {
              [styles.active]: _.get(fontStyles1, 'underline', false),
            })}
            onClick={() => handleFontStyleToggle('underline')}
            tooltip="Underline"
          >
            <i className="cleverstory-underline" />
          </Property.Action>
        </div>
      </Property>
      <Property name="Alignment" className={styles.alignmentProperty}>
        <div className={styles.options}>
        {["left", "center", "right"]
        .filter(pos => !(fontColor === "tertiaryColor" && pos === "center"))
        .map((pos) => (
          <Tooltip key={pos} placement="top" content={`${pos} Align`}>
            <button
              className={cx(styles.alignment, {
                [styles.active]: alignment === pos,
              })}
              onClick={() => handleAlignmentChange(pos)}
            >
              <i className={`cleverstory-align-${pos}`} />
            </button>
          </Tooltip>
        ))}
        </div>
      </Property>
    </>
  );
};

export const CustomizeSettings = React.memo(({
  id,
  widget,
  breakpoint,
  handlePropertyChange,
}) => {
  const [activeTab, setActiveTab] = useState("bar");
  const dispatch = useDispatch();
  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const deviceProperties = _.get(widget, `properties.${breakpoint}`, {});
  const questionDesign = _.get(widget, `properties.${breakpoint}.questionDesign`, {});
  const barProperties = _.get(questionDesign, `bar`, {});
  const iconProperties = _.get(barProperties, `icon`, {});
  const openAi = _.get(barProperties, `openAi`, {});
  const iconEnabled = _.get(iconProperties, `enabled`, true);
  const iconContent = _.get(iconProperties, "content")?.trim() || "cleverstory-paperflite-logo";

  const inputFieldColor = _.get(barProperties, `inputFieldColor`, {
    r: 255,
    g: 255,
    b: 255,
    a: 1,
  });
  const border = _.get(barProperties, `border`, {});
  const borderColor = _.get(border, "color", null);
  const borderWidth = _.get(border, "width", 0);
  const borderRadius = _.get(border, "radius", {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0,
  });
  
  const placeholderQuestion = _.get(barProperties, `placeholderQuestion`, "Enter your message here");
  const fontStyles = _.get(deviceProperties, "font", {});
  const fontStyles1 = _.get(deviceProperties, "font.style", {});
  const fontFamily = _.get(deviceProperties, 'font.fontFamily', 'Montserrat,sans-serif');
  const tertiaryColor = _.get(fontStyles, "tertiaryColor", "#000000");
  const fontSize = _.get(fontStyles, "fontSize", 16);
  const lineHeight = _.get(fontStyles, "lineSpacing") ?? 1.2;
  const letterSpacing = _.get(fontStyles, "letterSpacing") ?? 0;
  const alignment = _.get(barProperties, "alignment") ?? "left";
  const primaryColor = _.get(fontStyles, "color", "#000000");
  const iconSize = _.get(iconProperties, `fontSize`, 8);
  const iconAlignment = _.get(iconProperties, "alignment") ?? "right";  
  const openAiColor = _.get(openAi, "color") ?? { type: "solid", color: { r: 189, g: 189, b: 189, a: 1 } };  
  const openAiSize = _.get(openAi, "fontSize", 10);
  const openAiAlignment = _.get(openAi, "alignment", "right");

  Fonts.useFont(_.get(widget, 'fonts') || []);

  const handleBorderStyles = (key, value) => {
    handlePropertyChange("questionDesign", {
      ...questionDesign,
      bar: {
        ...barProperties,
        border: {
          ...border,
          [key]: value,
        },
      },
    });
  };

  const handlePlaceholderChange = (e) => {
    handlePropertyChange("questionDesign", {
      ...questionDesign,
      bar: {
        ...barProperties,
        placeholderQuestion: e.target.value,
      },
    });
  };

  const handleFontStylesChange = (type, value) => {
    handlePropertyChange("font", {
      ...fontStyles,
      [type]: value,
    });
  };

  const handleFontStyleToggle = (styleKey) => {
    handlePropertyChange('font', {
      ..._.get(deviceProperties, 'font', {}),
      style: {
        ...fontStyles1,
        [styleKey]: !_.get(fontStyles1, styleKey, false),
      },
    });
  };

  const handleFontFamily = (value) => {
    var font = _.get(value, "font.id");
    var family = _.get(value, "font.fontFamily");
    if (!family) {
      return;
    }

    var request = {
      ...fontStyles,
      fontFamily: family,
    };

    handlePropertyChange("font", request);
    if (font) {
      dispatch(associateFontsWithWigdet(editorSession, id, [font]));
    }
  };

  const handleOpenAiPropertyChange = (type, value) => {
    handlePropertyChange("questionDesign", {
      ...questionDesign,
      bar: {
        ...barProperties,
        openAi: {
          ...openAi,
          [type]: value,
        },
      },
    });
  };

  const handleAlignmentChange = (value) => {
    const request = {
      questionDesign: {
        ...questionDesign,
        bar: {
          ...barProperties,
          alignment: value,
        },
      },
    };
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, request));
  };

  const updateIconProperty = (key, value) => {
    handlePropertyChange("questionDesign", {
      ...questionDesign,
      bar: {
        ...barProperties,
        icon: {
          ...iconProperties,
          [key]: value,
        },
      },
    });
  };  

  const handleInputColorChange = (color) => {
    handlePropertyChange("questionDesign", {
      ...questionDesign,
      bar: {
        ...barProperties,
        inputFieldColor: color,
      },
    });
  };
  
  const icons = [
    "cleverstory-search",
    "cleverstory-paperflite-logo",
    "cleverstory-seek-rocket",
    "cleverstory-seek-arrow",
  ];

  return (
    <div className={styles.customizeSettings}>
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Nav variant="pills" className={styles.customizeTabs}>
          <Nav.Item>
            <Nav.Link eventKey="bar" className={styles.customizeTabLink}>
              Bar
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="placeholder" className={styles.customizeTabLink}>
              Placeholder
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="icons" className={styles.customizeTabLink}>
              Icons
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content className={styles.customizeTabContent}>
          <Tab.Pane eventKey="bar">
            <div className={styles.tabPaneContent}>
              <Property.Color
                title={"Background Color"}
                className={styles.propertyItem}
                clearable={true}
                gradient={false}
                value={inputFieldColor}
                onChange={handleInputColorChange}
              />

              <Property.Color
                title={"Stroke Color"}
                className={styles.propertyItem}
                clearable={true}
                gradient={false}
                value={borderColor}
                onChange={(color) => handleBorderStyles("color", color)}
              />

              <Property name="Stroke Weight" className={styles.propertyItem}>
                <Property.Slider
                  min={0}
                  max={10}
                  step={1}
                  input
                  value={borderWidth}
                  onChange={(value) => handleBorderStyles("width", value)}
                />
              </Property>

              <Property name="Corner Radius" className={styles.propertyItem}>
                <Property.BorderRadius
                  value={borderRadius}
                  onChange={(value) => handleBorderStyles("radius", value)}
                />
              </Property>
            </div>
          </Tab.Pane>

          <Tab.Pane eventKey="placeholder">
            <div className={styles.tabPaneContent}>
              <Property name="Placeholder Question" className={styles.propertyItem}>
                <Property.Input
                  value={placeholderQuestion}
                  onChange={handlePlaceholderChange}
                  placeholder="Enter your message here"
                />
              </Property>

              <PlaceholderStyleOptions
                breakpoint={breakpoint}
                fontStyles1={fontStyles1}
                fontFamily={fontFamily}
                tertiaryColor={tertiaryColor}
                fontColor="tertiaryColor"
                fontSize={fontSize}
                lineHeight={lineHeight}
                letterSpacing={letterSpacing}
                alignment={alignment}
                handleFontStylesChange={handleFontStylesChange}
                handleFontFamily={handleFontFamily}
                handleAlignmentChange={handleAlignmentChange}
                handleFontStyleToggle={handleFontStyleToggle}
              />
            </div>
          </Tab.Pane>
          <Tab.Pane eventKey="icons">
            <div className={styles.tabPaneContent}>
              <div className={styles.enableButtonWrapper}>
                <label className={styles.enableLabel}>
                  Propeller
                </label>
                <Property.Switch
                  className={styles.enableButton}
                  value={iconEnabled}
                  onChange={() =>
                    updateIconProperty("enabled", !iconEnabled)
                  }
                />
              </div>
              <div className={styles.title}>
                Search Icons
              </div>           
              <div className={styles.iconPreview}>
              {icons.map((icon, index) => (
                <div
                  key={index}
                  className={`${styles.iconItem} ${
                    iconContent === icon ? styles.selected : ""
                  }`}
                  onClick={() => updateIconProperty("content", icon)}
                >
                  <i className={icon} />
                </div>
              ))}
              </div>
              <Property.Color
                title={"Color"}
                className={styles.propertyItem}
                value={primaryColor}
                onChange={(color) => {
                  handleFontStylesChange("color", color);
                }}
              />
              <Property name="Icon Size" className={styles.propertyItem}>
                <Property.Slider
                  min={8}
                  max={24}
                  step={1}
                  input
                  value={iconSize}
                  onChange={(size) => updateIconProperty("fontSize", size)}
                />
              </Property>
              <Property name="Alignment" className={styles.alignmentProperty}>
                <div className={styles.options}>
                  <Tooltip placement="top" content="Left Align">
                    <button className={cx(styles.alignment,
                      {
                        [styles.active]: iconAlignment === 'left'
                      })}
                      onClick={() => updateIconProperty("alignment", "left")}
                    >
                      <i className={cx('cleverstory-align-left')}></i>
                    </button>
                  </Tooltip>
                  <Tooltip placement="top" content="Right Align">
                    <button className={cx(styles.alignment,
                      {
                        [styles.active]: iconAlignment === 'right'
                      })}
                      onClick={() => updateIconProperty("alignment", "right")}
                    >
                      <i className={cx('cleverstory-align-right')}></i>
                    </button>
                  </Tooltip>
                </div>
              </Property>
              <div className={styles.sectionTitle}>Powered by Open AI</div>
              <Property.Color
                title={"Color"}
                className={styles.propertyItem}
                value={openAiColor}
                onChange={(color) => {
                  handleOpenAiPropertyChange("color", color);
                }}
              />
              <Property name="Font Size" className={styles.propertyItem}>
                <Property.Slider
                  min={8}
                  max={24}
                  step={1}
                  input
                  value={openAiSize}
                  onChange={(fontSize) => {
                    handleOpenAiPropertyChange("fontSize", fontSize);
                  }}
                />
              </Property>
              <Property name="Alignment" className={styles.alignmentProperty}>
                <div className={styles.options}>
                  <Tooltip placement="top" content="Left Align">
                    <button className={cx(styles.alignment,
                      {
                        [styles.active]: openAiAlignment === 'left'
                      })}
                      onClick={() => handleOpenAiPropertyChange('alignment', 'left')}
                    >
                      <i className={cx('cleverstory-align-left')}></i>
                    </button>
                  </Tooltip>
                  <Tooltip placement="top" content="Right Align">
                    <button className={cx(styles.alignment,
                      {
                        [styles.active]: openAiAlignment === 'right'
                      })}
                      onClick={() => handleOpenAiPropertyChange('alignment', 'right')}
                    >
                      <i className={cx('cleverstory-align-right')}></i>
                    </button>
                  </Tooltip>
                </div>
              </Property>
            </div>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
});

export const PromptsProcessSettings = React.memo(({
  id,
  widget,
  breakpoint,
  handlePropertyChange,
}) => {
  const [activeTab, setActiveTab] = useState("prompts");
  const dispatch = useDispatch();
  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const questionDesign = _.get(widget, `properties.${breakpoint}.questionDesign`, {});
  const barProperties = _.get(questionDesign, `bar`, {});
  const responseDesign = _.get(widget, `properties.${breakpoint}.responseDesign`, {});

  const promptEnabled = _.get(barProperties, `promptEnabled`, false);
  const prompts = _.get(barProperties, `prompts`, [
    "What are your core business values?",
    "What are the latest industry trends?",
    "Can you define your competitive..."
  ]);
  const processingMessage = _.get(responseDesign, `processingMessage`, "Processing...");
  const processingMessageColor =
  _.get(responseDesign, "processingMessageColor") ?? {
    type: "solid",
    color: { r: 0, g: 0, b: 0, a: 1 }
  };

  const deviceProperties = _.get(widget, `properties.${breakpoint}`, {});

  const fontStyles = _.get(deviceProperties, "font", {});
  const secondaryColor = _.get(fontStyles, "secondaryColor", "#000000");
  const alignment = _.get(barProperties, "promptStyle.alignment", "center");
  const promptFont = _.get(barProperties, "promptStyle.font", {});
  const promptStyle = _.get(promptFont, "style", {});
  const fontFamily = _.get(promptFont, 'fontFamily', 'Montserrat,sans-serif');
  const fontSize = _.get(promptFont, 'fontSize', 16);
  const letterSpacing = _.get(promptFont, "letterSpacing") ?? 0;
  const lineHeight = _.get(promptFont, "lineSpacing") ?? 1.2;

  const togglePrompts = () => {
    handlePropertyChange("questionDesign", {
      ...questionDesign,
      bar: {
        ...barProperties,
        promptEnabled: !promptEnabled,
      },
    });
  };

  const handlePromptChange = (index, value) => {
    const updatedPrompts = [...prompts];
    updatedPrompts[index] = value;

    handlePropertyChange("questionDesign", {
      ...questionDesign,
      bar: {
        ...barProperties,
        prompts: updatedPrompts,
      },
    });
  };

  const handleProcessingMessageChange = (value) => {
    handlePropertyChange("responseDesign", {
      ...responseDesign,
      processingMessage: value,
    });
  };

  const handleFontStylesChange = (type, value) => {
    if (type === "secondaryColor") {
      return handlePropertyChange("font", {
        ...fontStyles,
        secondaryColor: value,
      });
    }
  
    if (type === "processingMessageColor") {
      return handlePropertyChange("responseDesign", {
        ...responseDesign,
        processingMessageColor: value,
      });
    }
  
    handlePropertyChange("questionDesign", {
      ...questionDesign,
      bar: {
        ...barProperties,
        promptStyle: {
          ...barProperties.promptStyle,
          font: {
            ...promptFont,
            [type]: value,
          },
        },
      },
    });
  };  
  
  const handleFontFamily = (value) => {
    var font = _.get(value, "font.id");
    var family = _.get(value, "font.fontFamily");
    if (!family) {
      return;
    }

    var request = {
      ...questionDesign,
      bar: {
        ...barProperties,
        promptStyle: {
          ...barProperties.promptStyle,
          font: {
            ...promptFont,
            fontFamily: family,
          },
        },
      },
    };    
    handlePropertyChange("questionDesign", request);

    if (font) {
      dispatch(associateFontsWithWigdet(editorSession, id, [font]));
    }
  };

  const handleAlignmentChange = (value) => {
    const request = {
      questionDesign: {
        ...questionDesign,
        bar: {
          ...barProperties,
          promptStyle: {
            ...barProperties.promptStyle,
            alignment: value,
          },
        },
      },
    };
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, request));
  };
  
  const handleFontStyleToggle = (styleKey) => {
    handlePropertyChange("questionDesign", {
      ...questionDesign,
      bar: {
        ...barProperties,
        promptStyle: {
          ...barProperties.promptStyle,
          font: {
            ...promptFont,
            style : {
              ...promptStyle,
              [styleKey]: !_.get(promptStyle, styleKey, false),
            }
          },
        },
      },      
    });
  };
  
  return (
    <div className={styles.promptsProcessSettings}>
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Nav variant="pills" className={styles.promptsProcessTabs}>
          <Nav.Item>
            <Nav.Link eventKey="prompts" className={styles.promptsProcessTabLink}>
              Prompts
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="processing" className={styles.promptsProcessTabLink}>
              Processing Text
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content className={styles.promptsProcessTabContent}>
          <Tab.Pane eventKey="prompts">
            <div className={styles.tabPaneContent}>
              <div className={styles.prompts}>
                <div className={styles.promptsWrapper}>
                  <label className={styles.promptEnabled}>
                    Prompts
                  </label>
                  <Property.Switch
                    className={styles.toggleSwitch}
                    value={promptEnabled}
                    onChange={togglePrompts}
                  />
                </div>
                <span className={styles.subMessage}>Suggested Prompts</span>
                {promptEnabled && (
                  <div className={styles.promptList}>
                    {_.map(prompts, (data, index) => {
                      return (
                        <div key={index} className={styles.promptItem}>
                          <label className={styles.promptLabel}>
                            Enter Prompt {index + 1}
                          </label>
                          <Form.Control
                            type="text"
                            className={styles.promptQuestion}
                            value={data}
                            onChange={(e) => handlePromptChange(index, e.target.value)}
                            placeholder={`Enter Prompt ${index + 1}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {promptEnabled && (
                <>
                  <div className={styles.divider} />
                  <div className={styles.sectionTitle}>
                    Prompt Settings
                  </div>
                  <PlaceholderStyleOptions
                    breakpoint={breakpoint}
                    fontStyles1={promptStyle}
                    fontFamily={fontFamily}
                    tertiaryColor={secondaryColor}
                    fontColor="secondaryColor"
                    fontSize={fontSize}
                    lineHeight={lineHeight}
                    letterSpacing={letterSpacing}
                    alignment={alignment}
                    handleFontStylesChange={handleFontStylesChange}
                    handleFontFamily={handleFontFamily}
                    handleAlignmentChange={handleAlignmentChange}
                    handleFontStyleToggle={handleFontStyleToggle}
                  />
                </>
              )}
            </div>
          </Tab.Pane>
          <Tab.Pane eventKey="processing">
            <div className={styles.tabPaneContent}>
              <Property className={styles.messageContainer} name="Processing Text" nameStyle={styles.processingName}
              >
                <span className={styles.subMessage}> Message while processing the result</span>
                <Form.Control
                  type="text"
                  as="textarea"
                  rows={3}
                  className={styles.processMessage}
                  value={processingMessage}
                  onChange={(e) => handleProcessingMessageChange(e.target.value)}
                  placeholder="Processing..."
                />
              </Property>
              <Property.Color
                title="Font Color"
                value={processingMessageColor}
                onChange={(color) => handleFontStylesChange("processingMessageColor", color)}
              />
            </div>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
});

export const SeekLayoutSettings = React.memo(({
  id,
  modal = false,
  breakpoint,
  handlePropertyChange,
  widget,
}) => {
  const questionDesign = _.get(
    widget,
    `properties.${breakpoint}.questionDesign`,
    {}
  );
  const layoutType = _.get(questionDesign, `type`, "bar");

  return (
    <div className={styles.seekLayoutSettings}>
      {layoutType === "bar" && (
        <BarStyleSettings
          id={id}
          widget={widget}
          breakpoint={breakpoint}
          handlePropertyChange={handlePropertyChange}
        />
      )}
      {layoutType === "button" && (
        <ButtonStyleSettings
          id={id}
          widget={widget}
          breakpoint={breakpoint}
          handlePropertyChange={handlePropertyChange}
        />
      )}
    </div>
  );
});

export const BarStyleSettings = React.memo(({
  id,
  widget,
  breakpoint,
  handlePropertyChange,
}) => {
  const properties = _.get(
    widget,
    `properties.${breakpoint}`,
    {}
  );
  const responseDesign = _.get(
    widget,
    `properties.${breakpoint}.responseDesign`,
    {}
  );
  const seekDesign = _.get(
    widget,
    `properties.${breakpoint}.seekDesign`,
    {}
  );
  const backgroundColor = _.get(responseDesign, `backgroundColor`);
  const seekBorder = _.get(seekDesign, `border`, {});
  const padding = _.get(properties, "padding", 0);
  const borderColor = _.get(seekBorder, "color", null);
  const borderWidth = _.get(seekBorder, "width", 0);
  const borderRadius = _.get(seekBorder, "radius", {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0,
  });

  const handleBackgroundColorChange = (color) => {
    handlePropertyChange("responseDesign", {
      ...responseDesign,
        backgroundColor: color,
    });
  };

  const handleBorderStyles = (key, value) => {
    handlePropertyChange("seekDesign", {
      ...seekDesign,
        border: {
          ...seekBorder,
          [key]: value,
        },
    });
  };

  const handlePaddingChange = (value) => {
    handlePropertyChange("padding", value);
  };

  return (
    <div className={styles.barCustomizationWrapper}>
      <Property.Color
        title={"Background Color"}
        className={styles.backgroundColorProperty}
        clearable={true}
        gradient={false}
        value={backgroundColor}
        onChange={(color) => {
          handleBackgroundColorChange(color);
        }}
      />
      <Property.Color
        title={"Stroke Color"}
        className={styles.borderColorProperty}
        clearable={true}
        gradient={false}
        value={borderColor}
        onChange={(color) => {
          handleBorderStyles("color", color);
        }}
      />
      <Property className={styles.borderWidth} name="Stroke Weight">
        <Property.Slider
          min={0}
          max={10}
          step={1}
          input
          value={borderWidth}
          onChange={(value) => {
            handleBorderStyles("width", value);
          }}
        />
      </Property>
      <Property name="Corner Radius" className={styles.buttonStyleProperty}>
        <Property.BorderRadius
          value={borderRadius}
          onChange={(value) => {
            handleBorderStyles("radius", value);
          }}
        />
      </Property>

        <Property name="Padding" className={styles.marginProperty}>
          <Property.Slider
            min={0}
            max={40}
            step={1}
            input
            value={padding}
            onChange={handlePaddingChange}
          />
        </Property>
    </div>
  );
});

const ButtonStyleSettings = ({ id, widget, onCancel }) => {
  return <div className={styles.buttonCustomizationWrapper}></div>;
};

export const ResponseDesignSettings = React.memo(({
  id,
  widget,
  breakpoint,
  handlePropertyChange,
}) => {
  const responseDesign = _.get(
    widget,
    `properties.${breakpoint}.responseDesign`,
    {}
  );
  const type = _.get(responseDesign, `type`, "bar");
  const backgroundColor = _.get(responseDesign, `backgroundColor`);
  const accentColor = _.get(responseDesign, `accentColor`);
  const responseColor =
  _.get(responseDesign, "responseColor") || {
    type: "solid",
    color: { r: 33, g: 37, b: 41, a: 1 }
  };
  const processingMessage = _.get(
    responseDesign,
    `processingMessage`,
    "Processing..."
  );
  const noResultsMessage = _.get(
    responseDesign,
    `noResultsMessage`,
    "No results found"
  );

  const responseLayoutOption = [
    {
      id: 1,
      label: "Inline",
      value: "inline",
      icon: "/images/palette/seek/layout/inline.png",
    },
    {
      id: 2,
      label: "Pop-Up",
      value: "popup",
      icon: "/images/palette/seek/layout/popUp.png",
    },
    {
      id: 3,
      label: "Slider",
      value: "slider",
      icon: "/images/palette/seek/layout/slider.png",
    },
  ];

  const handleBackgroundColorChange = (color) => {
    handlePropertyChange("responseDesign", {
      ...responseDesign,
      backgroundColor: color,
    });
  };

  const handleAccentColorChange = (color) => {
    handlePropertyChange("responseDesign", {
      ...responseDesign,
      accentColor: color,
    });
  };

  const handleResponseColorChange = (color) => {
    handlePropertyChange("responseDesign", {
      ...responseDesign,
      responseColor: color,
    });
  };

  const handleResponseStyle = (type) => {
    handlePropertyChange("responseDesign", {
      ...responseDesign,
      type: type,
    });
  };


  return (
    <div className={styles.responsiveDesignSettings}>
      <div className={styles.seekTitle}>Choose the experience that best showcases your content and resonates with your audience.</div>
      {breakpoint === "desktop" && (
        <div className={styles.modalStyleList}>
          {_.map(responseLayoutOption, (option, index) => {
            return (
              <div
                key={index}
                onClick={() => handleResponseStyle(option.value)}
                className={cx(styles.option, {
                  [styles.active]: option.value == type,
                })}
              >
                <img className={styles.iconImg} src={option.icon} />
                <span className={styles.label}>{option.label}</span>
              </div>
            );
          })}
        </div>
      )}
      <div className={styles.seekContent}>Present your assets in a vertical tile wall, complete with a customizable cover page.</div>
      <Property.Color
        title={"Background Color"}
        value={backgroundColor}
        onChange={handleBackgroundColorChange}
      />
      <Property.Color
        title={"Accent Color"}
        clearable={true}
        value={accentColor}
        onChange={handleAccentColorChange}
      />
      <Property.Color
        title={"Text Color"}
        clearable={true}
        className={styles.accentColorProperty}
        value={responseColor}
        onChange={handleResponseColorChange}
      />
    </div>
  );
});
